import { AgentStage } from "@src/appV2/Agents";
import { NodeEnvironment, environmentConfig } from "@src/appV2/environment";
import { logApiFailureEvent } from "@src/lib/analytics";
import request from "superagent";
import { z } from "zod";

import { getAuthHeader } from "../../superagent";

const latenessConfigSchema = z.array(
  z.object({
    points: z.number(),
    minutesLateRange: z.object({
      startFrom: z.number(),
      stopAt: z.number().optional(),
    }),
  })
);

export const attendanceScoreProfileResponseSchema = z.object({
  score: z.number(),
  suspensionCount: z.number().optional(),
  latenessPointsDeductedInLast30Days: z.number().optional().default(0),
  accountStatus: z.object({
    status: z
      .enum([
        AgentStage.ONBOARDING,
        AgentStage.ENROLLED,
        AgentStage.PROBATION,
        AgentStage.DEACTIVATED,
        AgentStage.RESTRICTED,
      ])
      .nullish(),
    reEnrollmentDate: z.string().optional(),
  }),
  policy: z
    .object({
      workShift: z
        .object({
          points: z.number(),
        })
        .optional(),
      suspension: z
        .array(
          z.object({
            score: z.tuple([z.number().nullable(), z.number().nullable()]),
            duration: z.number(),
          })
        )
        .optional(),
      cancelShift: z
        .array(
          z.object({
            points: z.number(),
            leadTime: z.array(z.number().nullable()),
          })
        )
        .optional(),
      latenessConfig: latenessConfigSchema.optional(),
      shiftClockInConfig: z
        .object({
          points: z.number(),
        })
        .optional(),
      deactivation: z
        .object({
          window: z.number(),
          duration: z.number(),
          suspension: z.number(),
        })
        .optional(),
      noCallNoShow: z
        .object({
          points: z.number(),
        })
        .optional(),
    })
    .optional(),
});

export type AttendanceScoreProfileResponse = z.infer<typeof attendanceScoreProfileResponseSchema>;

export type LatenessConfigResponse = z.infer<typeof latenessConfigSchema>;

export function getAttendanceScoreProfilePath(agentId: string): string {
  return `/workers/${agentId}/profile`;
}

// FIXME: Migrate this hook to appV2 and rework to use useGetQuery
// Also, remove schema parsing as it is done implictly in appV2
export const getAttendanceScoreProfile = async (
  agentId: string
): Promise<AttendanceScoreProfileResponse> => {
  try {
    const { body } = await request
      .get(
        `${environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL}${getAttendanceScoreProfilePath(
          agentId
        )}`
      )
      .set(await getAuthHeader());

    if (process.env.NODE_ENV !== NodeEnvironment.DEVELOPMENT) {
      return body;
    }

    return attendanceScoreProfileResponseSchema.parse(body);
  } catch (error) {
    logApiFailureEvent(error);
    throw error;
  }
};
